import { computed } from 'vue';
import { useStore } from 'vuex';

/**
 * Corporate branding composable that provides access to branding-related getters and logic
 */
export function useCorporateBranding() {
  const store = useStore();

  // Corporate branding getters
  const brandingData = computed(() => store.getters['corporateBranding/getBrandingInfo']);
  const hasEventBranding = computed(() => store.getters['corporateBranding/hasEventBranding']);
  const hasBrandingLogo = computed(() => store.getters['corporateBranding/hasBrandingLogo']);
  const getBrandingLogos = computed(() => store.getters['corporateBranding/getBrandingLogos']);
  const getAllBrandingLogos = computed(() => store.getters['corporateBranding/getAllBrandingLogos']);
  const getBrandingLogosFetched = computed(() => store.getters['corporateBranding/getBrandingLogosFetched']);

  // Field configuration helpers
  const hasAccountType = computed(() => {
    if (brandingData.value?.RegistrationFields) {
      const index = brandingData.value.RegistrationFields.findIndex(
        field => field.Name === 'AccountType'
      );
      return index !== -1 ? !brandingData.value.RegistrationFields[index].IsDeleted : true;
    }
    return true;
  });

  const requirePhoneNumber = computed(() => {
    if (brandingData.value?.RegistrationFields) {
      const index = brandingData.value.RegistrationFields.findIndex(
        field => field.Name === 'PhoneNumber'
      );
      return index !== -1 ? !brandingData.value.RegistrationFields[index].IsDeleted : true;
    }
    return true;
  });

  // Check if there are any custom form fields
  const hasFormFields = computed(() => {
    if (!brandingData.value?.RegistrationFields) return false;
    
    const index = brandingData.value.RegistrationFields.findIndex(field => 
      field.Name !== 'AccountType' && 
      field.Name !== 'CompanyRole' && 
      field.Name !== 'PhoneNumber' && 
      !field.IsDeleted
    );
    return index !== -1;
  });

  // Check if there are optional fields
  const hasOptionalFields = computed(() => {
    if (!brandingData.value?.RegistrationFields) return false;
    
    return brandingData.value.RegistrationFields.some(field => 
      !field.Required && !field.IsDeleted
    );
  });

  // Get additional instructions
  const getAdditionalInstructions = computed(() => {
    if (!brandingData.value?.CustomMessages) return null;
    
    const instructionsIndex = brandingData.value.CustomMessages.findIndex(msg => 
      msg.Key === 'AdditionalInstructions' && msg.Value !== ''
    );
    
    return instructionsIndex !== -1 ? brandingData.value.CustomMessages[instructionsIndex] : null;
  });

  // Get field configuration by name
  const getFieldConfig = (fieldName) => {
    if (!brandingData.value?.RegistrationFields) return null;
    
    const fieldIndex = brandingData.value.RegistrationFields.findIndex(
      field => field.Name === fieldName && !field.IsDeleted
    );
    
    return fieldIndex !== -1 ? brandingData.value.RegistrationFields[fieldIndex] : null;
  };

  return {
    // Getters
    brandingData,
    hasEventBranding,
    hasBrandingLogo,
    getBrandingLogos,
    getAllBrandingLogos,
    getBrandingLogosFetched,
    hasAccountType,
    requirePhoneNumber,
    hasFormFields,
    hasOptionalFields,
    getAdditionalInstructions,
    
    // Methods
    getFieldConfig,
  };
}
